import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API, formatIDR, publicFileUrl } from "../lib/api";
import { BRAND_META } from "../context/BrandContext";
import { ArrowLeft, ShareNetwork, ShoppingBag } from "@phosphor-icons/react";
import { shareProduct, imageAtIndexAsFile } from "../lib/share";
import { formatProductName } from "../lib/productName";
import { shopeeUrl, ShopeeLogo } from "../lib/marketplace";

// Global Hoshizora brand content, rendered on every Hoshizora public detail page.
const HOSHIZORA_GLOBAL = [
    "HOSHIZORA QUALITY\nJapanese Lab Moissanite\nHypoallergenic\nLifetime Warranty",
    "CARE & WARRANTY\nMade to be worn and enjoyed every day. For lasting brilliance, we recommend reducing frequent contact with perfume, alcohol, lotions, and harsh chemicals. Your Hoshizora jewelry is supported by our lifetime 925 Silver warranty and dedicated after-sales service.",
    "WHAT’S INCLUDED\nHoshizora Jewelry\nHoshizora Jewelry Box\nWarranty Card\nCertificate\nPremium Silver Cleaning Cloth",
];
const GLOBAL_HEADINGS = ["HOSHIZORA QUALITY", "CARE & WARRANTY", "WHAT'S INCLUDED"];

// Display-only: drop blocks whose heading is one of the global sections to avoid duplicates.
const stripGlobalSections = (text) =>
    (text || "")
        .split(/\n\s*\n/)
        .filter((b) => !GLOBAL_HEADINGS.includes(b.trim().split("\n")[0].trim().toUpperCase().replace(/[’`]/g, "'")))
        .join("\n\n")
        .trim();

export default function PublicProductDetail({ brand }) {
    const meta = BRAND_META[brand] || { name: brand, accent: "#6F7D69", bg: "#F8F6F2" };
    const { id } = useParams();
    const [p, setP] = useState(null);
    const [active, setActive] = useState(0);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/products/${id}`, { params: { brand } });
                setP(data);
            } catch {
                setNotFound(true);
            }
        })();
    }, [id, brand]);

    if (notFound) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: meta.bg }}>
                <div className="text-center">
                    <p className="font-serif text-3xl">Product not found</p>
                    <Link to={`/${brand}`} className="inline-flex items-center gap-1 mt-3 text-sm underline">
                        <ArrowLeft size={14} weight="light" /> Back to catalog
                    </Link>
                </div>
            </div>
        );
    }

    if (!p) return <div className="min-h-screen flex items-center justify-center text-sm text-neutral-400" style={{ background: meta.bg }}>Loading…</div>;

    const imgs = p.images?.length ? p.images : [];
    const main = imgs[active] || imgs[0];
    const hasVariants = p.variants?.length > 0;
    const isHoshizora = brand === "hoshizora";
    const desc = isHoshizora ? stripGlobalSections(p.description) : p.description;
    const knowledge = isHoshizora ? stripGlobalSections(p.product_knowledge) : p.product_knowledge;
    const hasDescription = Boolean(desc) || Boolean(knowledge);
    const populatedExtras = [
        ["Material", "material"], ["Stone Type", "stone_type"],
        ["Stone Grade", "stone_grade"], ["Stone Color", "stone_color"],
        ["Stone Cut", "stone_shape"],
        ...(brand === "hoshizora" ? [["Carat", "carat"]] : []),
    ].filter(([, k]) => Boolean(p[k]));
    const toUrl = (u) => {
        const s = (u || "").trim();
        if (!s || /\s/.test(s) || !s.includes(".")) return null;
        return /^https?:\/\//i.test(s) ? s : `https://${s}`;
    };
    const shopee = shopeeUrl(p.shopee_url);
    const marketLinks = isHoshizora
        ? [["Shop on Tokopedia", toUrl(p.tokopedia_url), "tokopedia"]].filter(([, u]) => u)
        : [];
    const descriptionBlock = hasDescription && (
        <div className="pt-4 border-t border-black/10 font-sans">
            {desc && (
                <p className="text-neutral-700 whitespace-pre-wrap text-sm md:text-base leading-relaxed" data-testid="public-description">{desc}</p>
            )}
            {knowledge && (
                <div className={desc ? "mt-4" : ""} data-testid="public-knowledge">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Selling Points</div>
                    <p className="text-neutral-700 whitespace-pre-wrap text-sm md:text-base leading-relaxed">{knowledge}</p>
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen w-full" style={{ background: meta.bg }}>
            <header className="border-b border-black/5 bg-white/50 backdrop-blur">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 flex items-center justify-between gap-4">
                    <Link to={`/${brand}`} data-testid="public-back-link" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-600 hover:text-ink">
                        <ArrowLeft size={14} weight="light" /> Back to Catalog
                    </Link>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
                <div className="grid md:grid-cols-2 gap-6 md:gap-12">
                    <div>
                        <div className="aspect-square bg-black/[0.03] overflow-hidden rounded-sm flex items-center justify-center p-6">
                            {main?.path ? (
                                <img src={publicFileUrl(main.path)} alt={p.name} className="w-full h-full object-contain" />
                            ) : (
                                <div className="text-neutral-300 text-xs uppercase tracking-widest">No photo</div>
                            )}
                        </div>
                        {imgs.length > 1 && (
                            <div className="grid grid-cols-5 gap-2 mt-3">
                                {imgs.map((im, i) => (
                                    <button key={im.id || i} onClick={() => setActive(i)} className={`aspect-square bg-black/[0.03] rounded-sm overflow-hidden border p-1.5 ${active === i ? "border-black/40" : "border-black/10"}`} data-testid={`public-thumb-${i}`}>
                                        <img src={publicFileUrl(im.path)} alt="" className="w-full h-full object-contain" loading="lazy" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="space-y-5 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{p.category}</div>
                        <h1 className="font-sans font-medium text-xl md:text-2xl leading-[1.3] tracking-tight break-words" style={{ color: meta.accent }} data-testid="public-detail-title">
                            {formatProductName(p.name)}
                        </h1>
                        {p.sku && <div className="font-mono text-sm font-medium text-ink" data-testid="public-detail-sku">{p.sku}</div>}

                        <div className="font-sans">
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Retail Price</div>
                            <div className="font-sans font-semibold tabular-nums text-2xl md:text-3xl mt-1 tracking-tight" data-testid="public-detail-price" style={{ color: meta.accent }}>
                                {formatIDR(p.retail_price)}
                            </div>
                        </div>
                        {(shopee || marketLinks.length > 0) && (
                            <div className="flex flex-wrap items-center gap-2 font-sans" data-testid="public-market-links">
                                {shopee && (
                                    <a href={shopee} target="_blank" rel="noopener noreferrer" aria-label="Shop on Shopee" title="Shop on Shopee"
                                        data-testid="public-market-shopee"
                                        className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-black/10 bg-white hover:border-black/30 transition-colors">
                                        <ShopeeLogo size={24} />
                                    </a>
                                )}
                                {marketLinks.map(([label, url, key]) => (
                                    <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                                        data-testid={`public-market-${key}`}
                                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-widest text-white hover:opacity-90 transition-opacity"
                                        style={{ background: meta.accent }}>
                                        <ShoppingBag size={14} weight="light" /> {label}
                                    </a>
                                ))}
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={async () => {
                                // Share the image the viewer is currently looking at. If the
                                // user hasn't picked a thumbnail yet, `active` is 0 (main image).
                                const imageOverride = await imageAtIndexAsFile(p, active);
                                await shareProduct(p, { imageOverride });
                            }}
                            data-testid={`public-share-${p.sku || p.id}`}
                            className="inline-flex items-center gap-2 border border-black/15 rounded-full px-4 py-2 text-xs uppercase tracking-widest hover:bg-white"
                        >
                            <ShareNetwork size={14} weight="light" /> Share {imgs.length > 1 ? "this photo" : ""}
                        </button>

                        {!isHoshizora && descriptionBlock}
                    </div>
                </div>

                {hasVariants && (
                    <section className="mt-10 md:mt-14 max-w-4xl">
                        <h2 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">Variants · {p.variants.length}</h2>
                        <div className="border border-black/10 rounded-sm overflow-hidden bg-white">
                            <table className="hidden md:table w-full text-sm font-sans">
                                <thead>
                                    <tr className="text-left border-b border-black/10 bg-black/[0.02]">
                                        <th className="p-3 text-[10px] uppercase tracking-widest text-neutral-500">Variant / Size</th>
                                        <th className="p-3 text-[10px] uppercase tracking-widest text-neutral-500">SKU</th>
                                        <th className="p-3 text-[10px] uppercase tracking-widest text-neutral-500">Carat</th>
                                        <th className="p-3 text-[10px] uppercase tracking-widest text-neutral-500">Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {p.variants.map((v) => (
                                        <tr key={v.id} className="border-b border-black/5 last:border-0">
                                            <td className="p-3 font-medium">{v.label}</td>
                                            <td className="p-3 font-mono text-xs">{v.sku || "—"}</td>
                                            <td className="p-3 tabular-nums">{v.carat || "—"}</td>
                                            <td className="p-3 tabular-nums font-medium">{formatIDR(v.retail_price)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <ul className="md:hidden divide-y divide-black/5">
                                {p.variants.map((v) => (
                                    <li key={v.id} className="p-3 font-sans">
                                        <div className="font-medium">{v.label}</div>
                                        {v.sku && <div className="font-mono text-[11px] text-neutral-500 mt-0.5">{v.sku}</div>}
                                        <div className="text-xs text-neutral-500 mt-0.5">{v.carat ? `${v.carat} CT` : null}</div>
                                        <div className="mt-2 flex items-end justify-between">
                                            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Price</div>
                                            <div className="tabular-nums font-medium">{formatIDR(v.retail_price)}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>
                )}

                {populatedExtras.length > 0 && (
                    <section className="mt-10 md:mt-14 max-w-4xl">
                        <h2 className="font-serif text-2xl md:text-3xl tracking-tight mb-4">Details</h2>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm font-sans">
                            {populatedExtras.map(([label, key]) => (
                                <div key={key} className="border-b border-black/10 pb-3">
                                    <dt className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</dt>
                                    <dd className="mt-1 break-words">{p[key]}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                )}
                {isHoshizora && hasDescription && (
                    <section className="mt-10 md:mt-14 max-w-4xl" data-testid="public-description-section">{descriptionBlock}</section>
                )}
                {isHoshizora && (
                    <section className="mt-10 md:mt-14 max-w-4xl" data-testid="public-hoshizora-global">
                        <div className="pt-4 border-t border-black/10 font-sans">
                            <p className="text-neutral-700 whitespace-pre-wrap text-sm md:text-base leading-relaxed">{HOSHIZORA_GLOBAL.join("\n\n")}</p>
                        </div>
                    </section>
                )}
            </main>

            <footer className="border-t border-black/5 bg-white/40 mt-10">
                <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 text-xs text-neutral-500 flex items-center justify-between">
                    <span>© {new Date().getFullYear()} {meta.name}</span>
                    <Link to="/" className="hover:text-neutral-700 uppercase tracking-widest">Choose catalog</Link>
                </div>
            </footer>
        </div>
    );
}
